import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/aura`;
export const AURA_API = BASE;

// Browser storage can be unavailable (private mode, blocked site data) — never let that break the chat.
export const storage = {
  get(kind, key) {
    try {
      return window[kind].getItem(key);
    } catch {
      return null;
    }
  },
  set(kind, key, value) {
    try {
      window[kind].setItem(key, value);
    } catch {
      /* ignore */
    }
  },
};

const randomId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Stable per-browser id so Simmi can remember returning visitors.
export function getVisitorId() {
  let id = storage.get("localStorage", "aura_visitor_id");
  if (!id) {
    id = randomId();
    storage.set("localStorage", "aura_visitor_id", id);
  }
  return id;
}

export const auraApi = {
  config: () => axios.get(`${BASE}/config`).then((r) => r.data),
  chat: (payload, signal) => axios.post(`${BASE}/chat`, payload, { signal }).then((r) => r.data),
  event: (sessionId, body) => axios.post(`${BASE}/sessions/${sessionId}/events`, body).then((r) => r.data),
  greeting: (visitorId, language = "en") =>
    axios.get(`${BASE}/greeting`, { params: { visitor_id: visitorId, language }, timeout: 9000 }).then((r) => r.data),
  // Streams Simmi's reply: onDelta(text) for each chunk as it's written, onStatus(key) when she starts a tool
  // ("show_card", "thinking"…); resolves with the final {reply, actions, session_id, lead, meeting_booked}.
  // `signal` cancels it (the visitor interrupted her).
  chatStream: async (payload, onDelta, signal, onStatus) => {
    const res = await fetch(`${BASE}/chat/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
    });
    if (!res.ok || !res.body) throw new Error(`Simmi stream failed: ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let done = null;
    for (;;) {
      const { value, done: finished } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });
      let cut;
      while ((cut = buffer.indexOf("\n\n")) !== -1) {
        const line = buffer.slice(0, cut).trim();
        buffer = buffer.slice(cut + 2);
        if (!line.startsWith("data:")) continue;
        const event = JSON.parse(line.slice(5));
        if (event.type === "delta") onDelta(event.text);
        else if (event.type === "status") onStatus?.(event.key);
        else if (event.type === "done") done = event;
      }
    }
    if (!done) throw new Error("Simmi stream ended early");
    return done;
  },
  avatarPreview: () => axios.get(`${BASE}/avatar/preview`).then((r) => r.data),
  // A meeting request ({name, email, phone, preferred_time, topic, source, page, session_id}); resolves with
  // {ok: true, id} or {ok: false, reason} (a reason Simmi can say to the visitor).
  bookMeeting: (booking) => axios.post(`${BASE}/meetings`, booking).then((r) => r.data),
  // A session in this language: Anam listens for it and (with a Hindi voice configured) speaks it.
  avatarSession: (language = "en") =>
    axios.post(`${BASE}/avatar/session`, null, { params: { language } }).then((r) => r.data.session_token),
};

export { randomId };
